// MODEL-POLICY: the Setup › Model policy query. The key sits under `["company", co, …]`, so the
// company's change pings (a `company` subject: `company.model_policy_changed`, `company.updated`)
// refresh it.
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type ModelPolicyView = OperationOutput<"modelPolicy.get">;
export type ModelPolicy = ModelPolicyView["policy"];
export type ModelPolicyPut = OperationInput<"modelPolicy.put">["body"];
export type PolicyOverride = ModelPolicyView["overrides"]["data"][number];

export const modelPolicyKey = (co: string) => ["company", co, "model-policy"] as const;

// RUN-EFFORT: the effort tiers (`GET`/`PUT …/run-effort`), admin-only like the policy.
export type RunEffortView = OperationOutput<"runEffort.get">;
export type RunEffortPut = OperationInput<"runEffort.put">["body"];
export type EffortTier = RunEffortView["tiers"][number]["tier"];
export type Reasoning = RunEffortView["tiers"][number]["reasoning"];
export type EffortAgent = RunEffortView["agents"][number];
export type AgentOverride = NonNullable<EffortAgent["override"]>;

export const runEffortKey = (co: string) => ["company", co, "run-effort"] as const;

export const runEffortQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: runEffortKey(co),
    queryFn: () => api.client.runEffort.get({ params: { co } }),
  });

/** The stored map of a view: only the tiers the company set, and every agent override. */
export function effortBodyOf(v: RunEffortView): RunEffortPut {
  const tiers = Object.fromEntries(
    v.tiers
      .filter((t) => t.custom)
      .map((t) => [t.tier, { model: t.model, reasoning: t.reasoning }]),
  );
  const forAgent = Object.fromEntries(
    v.agents.flatMap((a) => (a.override ? [[a.id, a.override]] : [])),
  );
  return {
    ...(Object.keys(tiers).length ? { tiers } : {}),
    ...(v.refine_with_classifier ? { refine_with_classifier: true } : {}),
    ...(Object.keys(forAgent).length ? { for_agent: forAgent } : {}),
  };
}

export const modelPolicyQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: modelPolicyKey(co),
    queryFn: () => api.client.modelPolicy.get({ params: { co } }),
  });

/** The models a draft policy routes (routing.ts `allowedModels`, over the page's gateway list). */
export function routableOf(view: ModelPolicyView, draft: Draft): string[] {
  const allow = draft.allowAll ? null : new Set(draft.allowed);
  return view.gateway.models
    .filter(
      (m) =>
        (allow === null || allow.has(m.alias)) &&
        (!draft.residency || m.regions.includes(draft.residency)),
    )
    .map((m) => m.alias);
}

/** The form's state: the policy with "every gateway model" and "no default" made explicit. */
export type Draft = {
  residency: string;
  allowAll: boolean;
  allowed: string[];
  defaultModel: string;
  kinds: Record<string, string>;
};

export function draftOf(p: ModelPolicy): Draft {
  return {
    residency: p.residency ?? "",
    allowAll: !p.allowed_models,
    allowed: p.allowed_models ?? [],
    defaultModel: p.default_model ?? "",
    kinds: { ...(p.role_defaults ?? {}) },
  };
}

export function bodyOf(d: Draft): ModelPolicyPut {
  const kinds = Object.entries(d.kinds).filter(([, m]) => m.trim());
  return {
    residency: d.residency || null,
    allowed_models: d.allowAll || d.allowed.length === 0 ? null : d.allowed,
    default_model: d.defaultModel.trim() || null,
    role_defaults: Object.fromEntries(kinds.map(([k, m]) => [k, m.trim()])),
  };
}

/** Equal as stored: allow-list and task-kind order do not count. */
export function sameDraft(a: Draft, b: Draft): boolean {
  const canon = (d: Draft) => {
    const x = bodyOf(d);
    return JSON.stringify([
      x.residency,
      x.allowed_models ? [...x.allowed_models].sort() : null,
      x.default_model,
      Object.entries(x.role_defaults).sort(([p], [q]) => (p < q ? -1 : 1)),
    ]);
  };
  return canon(a) === canon(b);
}
